// Pure derivation of a building tile's status chip(s) for the shared kanta/salkku overview
// (docs/spec/concepts.md §2.4 → "Portfolio-scope overview grammar" → building-tile
// contract). Split out from OverviewBuildingList.tsx so the label/tone selection is testable
// directly (bun:test) — the same split ActivityFeed.tsx uses for its filter logic, since no React
// component-test infra (no @testing-library/react, no DOM environment) exists in this repo.
import type { OverviewBucket, PerBuildingOverview } from "@/lib/overview-lens";

export type TileStatusKind = "loading" | "unknown" | "complete" | "open";

export interface TileStatusChip {
  key: string;
  label: string;
}

export interface TileStatus {
  kind: TileStatusKind;
  /** One synthetic chip for loading/unknown/complete; one real chip per open question — labelled
   *  identically to that question's situation card — when `kind === "open"`. */
  chips: TileStatusChip[];
}

/**
 * `overview` is `undefined` while that building's per-building state hasn't settled yet
 * (progressive fan-out) — distinct from `dataAvailable === false`, where every underlying fetch
 * for that building failed. Neither is ever rendered as complete.
 */
export function tileStatus(
  overview: PerBuildingOverview | undefined,
  completeLabel: string,
  bucketByKey: Map<string, OverviewBucket>,
): TileStatus {
  if (!overview) {
    return { kind: "loading", chips: [{ key: "loading", label: "Ladataan…" }] };
  }
  if (!overview.dataAvailable) {
    return {
      kind: "unknown",
      chips: [{ key: "unknown", label: "Tuntematon" }],
    };
  }
  if (overview.complete || overview.openQuestions.length === 0) {
    return {
      kind: "complete",
      chips: [{ key: "complete", label: completeLabel }],
    };
  }
  return {
    kind: "open",
    chips: overview.openQuestions.map((key) => ({
      key,
      label: bucketByKey.get(key)?.label ?? key,
    })),
  };
}
